import React, { useMemo, useState, useEffect } from 'react';
import { Form } from 'semantic-ui-react';
import { Reservations, Hotels } from '../../api/services/next';
import { DatePicker } from '../../components/shared/DatePicker';
import HotelsDropDown from '../../components/shared/HotelsDropDown';
import TextInput from '../../components/shared/TextInput';
import { useTextResources } from '../../language';
import GridSearchTemplate from '../GridSearchTemplate';
import moment from 'moment';
import { endpoint } from '../../api/http';
import CustomerDropDown from '../../components/shared/CustomerDropDown';
import ExternalChannelDropDown from '../../components/shared/ExternalChannelDropDown';
import ReservationStatusesDropDown from '../../components/shared/ReservationStatusesDropDown';
import GuestSearchDropDown from '../../components/shared/GuestSearchDropDown';
import RatesDropDown from '../../components/shared/RatesDropDown';
import { Checkbox } from 'semantic-ui-react';
import ReservationTypesDropDown from '../../components/shared/ReservationTypesDropDown';
import RoomTypesDropDown from '../../components/shared/RoomTypesDropDown';
import PropTypes from 'prop-types';
import { dateFormat } from '../../components/shared/constants';
import { Loader } from 'semantic-ui-react';
import { onError } from '../../components/shared/messages';
import ReservationPoliciesDropDown from '../../components/shared/ReservationPoliciesDropDown';
import ReservationPolicyPopup from '../../components/ReservationPolicyPopup';
import useBoolean from '../../hooks/useBoolean';
import { reservationNumberHref } from '../../components/shared/reservationNumberHref';

const ReservationSearch = ({ isUnassignedRoomsOnly, statuses }) => {
  const text = useTextResources();
  const [unassignedRoomsOnly, setUnassignedRoomsOnly] = useState(
    isUnassignedRoomsOnly
  );
  const [findSimilar, setFindSimilar] = useState(true);
  const [hotelIds, setHotelIds] = useState([]);
  const [permittedHotelIds, setPermittedHotelIds] = useState([]);
  const [guest, setGuest] = useState({});

  useEffect(() => {
    // Fetch permitted hotels on mount
    Hotels.GetHotels({
      IsActive: true,
      IncludeOnlyHotelsWithUserPermissions: true,
    }).then((hotels) => {
      if (Array.isArray(hotels)) {
        setPermittedHotelIds(hotels.map(h => h.Id));
      }
    }).catch((error) => {
      onError(error);
    });
  }, []);

  const [selectedReservation, setSelectedReservation] = useState();

  const policyPopupState = useBoolean()

  const openPolicyPopup = (reservation) => {
    setSelectedReservation(reservation)
    policyPopupState.setTrue();
  }

  const closePolicyPopup = () => {
    setSelectedReservation(undefined)
    policyPopupState.setFalse();
  }

  const dateFormatter = ({ value }) => {
    return value && moment(value).format(dateFormat);
  };

  const RoomTypesSpec = React.useMemo(() => {
    let spec = {
      HotelIds: hotelIds,
    };
    return spec;
  }, [hotelIds]);

  
  const reservationFormatter = ({ value, row }) => {
    return row ? (
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={reservationNumberHref(row)}
      >
        {value}
      </a>
    ) : value || "";
  };
  const guestFormatter = ({ value, row }) => {
    return row ? (
      <a
        target='_blank'
        rel='noopener noreferrer'
        href={`${endpoint}CRM/GuestProfile/index/?hotelid=${row.HotelId}#/profile/${row.GuestId}`}
      >
        {value}
      </a>
    ) : value || "";
  };
  const policyFormatter = ({ value, row }) => {
    return row && value ? (
      // row.PolicyId
      <a
        target='_blank'
        rel='noopener noreferrer'
        onClick={() => openPolicyPopup(row)}
        style={{
          cursor: 'pointer'
        }}
      // href={`${endpoint}CRM/GuestProfile/index/?hotelid=${row.HotelId}#/profile/${row.GuestId}`}
      >
        {value}
      </a>
    ) : value || "";
  };
  
  const CustomersSpec = React.useMemo(() => {
    return {
      GetOnlyCustomerArray: true,
      IsActive: true,
      IncludeCustomerTypeId: true,
    };
  }, []);

  const RatesSpec = React.useMemo(() => {
    return { hotelIds };
  }, [hotelIds]);

  const onChangeGuestvalue = (value) => {
    if (!value?.toString().startsWith('newguest-')) {
      setGuest({ GuestId: value });
    }
  };

  const datePredicate = (value, filter) => {
    return dateFormatter({ value })?.includes(filter?.value?.toLowerCase())
  }

  const gridOptions = {
    search: {
      fields: [
        <Form.Field
          key='HotelIds'
          onChange={setHotelIds}
          value={hotelIds}
          multiple
          label={text('Hotel')}
          control={HotelsDropDown}
          spec={useMemo(
            () => ({
              IsActive: true,
              IncludeOnlyHotelsWithUserPermissions: true,
            }),
            []
          )}
          placeholder={text('All')}
          clearable
        />,
        <Form.Field
          key='Number'
          label={text('ReservationNumber')}
          control={TextInput}
          buttonPlacement='none'
          digits={0}
        />,
        <DatePicker
          picktype='range'
          label={text('ArrivalDate')}
          key='CheckInDateRange'
          clearable
        />,
        <DatePicker
          picktype='range'
          label={text('DepartureDate')}
          key='CheckOutDateRange'
          clearable
        />,
        <Form.Field
          key='Statuses'
          label={text('ReservationStatus')}
          placeholder={text('All')}
          multiple
          clearable
          control={ReservationStatusesDropDown}
        />,
        <Form.Field
          key='GuestId'
          label={text('Guest')}
          width={3}
          clearable
          control={GuestSearchDropDown}
          placeholder='Search by name, email, phone or id'
          additionLabel={`${text('Search')}: `}
          onAddGuest={setGuest}
          onChange={onChangeGuestvalue}
        />,
        <Form.Field key='Similar'>
          <label>{text('FindSimilar')}</label>
          <Checkbox
            onChange={(e, { checked }) => setFindSimilar(checked)}
            checked={findSimilar}
          />
        </Form.Field>
      ],
      advancedFields: [
        <Form.Field
          key='RateId'
          label={text('Rate')}
          width={3}
          placeholder={text('All')}
          clearable
          control={RatesDropDown}
          spec={RatesSpec}
        />,
        <DatePicker
          picktype='range'
          label={text('CreateDate')}
          key='CreateDateRange'
          clearable
        />,
        <Form.Field
          key='CustomerID'
          label={text('Customer')}
          placeholder={text('All')}
          width={3}
          clearable
          control={CustomerDropDown}
          spec={CustomersSpec}
        />,
        <Form.Field
          key='ExternalChannelID'
          label={text('ExternalChannel')}
          placeholder={text('All')}
          clearable
          control={ExternalChannelDropDown}
        />,
        <Form.Field
          key='ExternalReservationMasterId'
          label={text('ExternalReservationMasterID')}
          control={TextInput}
        />,
        <Form.Field
          key='RoomTypeId'
          label={text('RoomType')}
          placeholder={text('All')}
          HotelIDs={hotelIds}
          control={RoomTypesDropDown}
          clearable
          spec={RoomTypesSpec}
        />,
        <Form.Field
          key='ReservationTypes'
          control={ReservationTypesDropDown}
          placeholder={text('All')}
          clearable
          multiple
          label={text('ReservationType')}
        />,
        <Form.Field key='UnassignedRoomsOnly'>
          <label>{text('ReservationsWithoutAllocatedRoom')}</label>
          <Checkbox
            onChange={(e, { checked }) => setUnassignedRoomsOnly(checked)}
            checked={unassignedRoomsOnly}
          />
        </Form.Field>,
        <Form.Field
          key='PolicyIds'
          control={ReservationPoliciesDropDown}
          placeholder={text('All')}
          clearable
          multiple
          searchByParent
          label={text('ReservationPolice')}
        />,
      ],
      method: 'POST',
      api: Reservations.Search,
      searchOnLoad: isUnassignedRoomsOnly,
      spec: {
        MaxResults: 1000,
        UnassignedRoomsOnly: unassignedRoomsOnly,
        FreeText:
          (guest?.FirstName || guest?.LastName) &&
          `${guest?.FirstName} ${guest?.LastName}`,
        GuestId: guest?.GuestId?.toString().startsWith('newguest-')
          ? null
          : guest?.GuestId,
        HotelIds: hotelIds.length > 0 ? hotelIds : permittedHotelIds,
      },
      initial: unassignedRoomsOnly
        ? {
          HotelIds: hotelIds.length > 0 ? hotelIds : permittedHotelIds,
          ExcludeBlockReservations: true,
          Statuses: statuses,
          IsUnassignedRoomsOnly: unassignedRoomsOnly
        }
        : null,
    },
    grid: {
      columns: [
        {
          name: 'Number',
          title: text('Number'),
          formatter: reservationFormatter,
        },
        { name: 'Hotel', title: text('Hotel') },
        {
          name: 'CheckInDate',
          title: text('ArrivalDate'),
          formatter: dateFormatter,
        },
        {
          name: 'CheckOutDate',
          title: text('DepartureDate'),
          formatter: dateFormatter,
        },
        { name: 'GuestName', title: text('Guest'), formatter: guestFormatter },
        { name: 'GuestPhone', title: text('GuestPhone') },
        { name: 'Company', title: text('Company') },
        { name: 'Agent', title: text('Agent') },
        { name: 'Room', title: text('Room') },
        { name: 'Status', title: text('Status') },
        { name: 'Product', title: text('Product') },
        { name: 'ReservationPolicyName', title: text('Policy'), formatter: policyFormatter },
      ],
      paging: true,
      scrollbarX:true,
      cacheId:'ReservationSearch',
        customOptions:{
        hidden: {
            toggleHidden:true
        },
        group: {
          grouping: [{ columnName: 'Hotel' }]
        },
            order:{},
            sort:{
              sorting:[{ columnName: 'Number', direction: 'desc' }]
        }
      },
      filtering: {
        showByDefault: true,
        extensions: [
          { columnName: 'CheckInDate', predicate: datePredicate },
          { columnName: 'CheckOutDate', predicate: datePredicate }
        ]
      },
    },
  };
  if (unassignedRoomsOnly && permittedHotelIds.length === 0) {
    return <Loader active inline="centered" content={text('Loading')} />;
  }
    return <React.Fragment>
        <GridSearchTemplate options={gridOptions} />
        {policyPopupState.value && selectedReservation && <ReservationPolicyPopup
            open={policyPopupState.value}
            onClose={closePolicyPopup}
            reservationId={selectedReservation.Id}
        />}
    </React.Fragment>
};

ReservationSearch.propTypes = {
  hotelId: PropTypes.any,
  isUnassignedRoomsOnly: PropTypes.bool,
  statuses: PropTypes.array
};
export default ReservationSearch;
